import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import { ReadalongScrollContainer } from '@/components/start/read-along/read-along-renders';

export function VirtualScrollContainer({
  children,
  scrollAreaProps,
  scrollContainerClassName,
  scrollContainerParentClassName,
  scrollHeader,
  scrollHeaderRef,
  scrollFooter,
  scrollMargin,
  scrollRef,
  contentRef,
  totalSize,
  translateY,
}: {
  children: ReactNode;
  /** Extra props for the scroll area root (e.g. user-gesture handlers). */
  scrollAreaProps?: Omit<
    ComponentPropsWithoutRef<typeof ReadalongScrollContainer>,
    'ref' | 'style'
  >;
  scrollContainerClassName?: string | undefined;
  scrollContainerParentClassName?: string | undefined;
  scrollHeader?: ReactNode;
  scrollHeaderRef?: Ref<HTMLDivElement>;
  /** Rendered after the virtualized content, outside the virtualizer's measurements. */
  scrollFooter?: ReactNode;
  scrollMargin?: number;
  scrollRef: Ref<HTMLDivElement>;
  /** The total-size spacer element; its height tracks measured content, so a
   * ResizeObserver on it sees font/spacing/zoom changes (see
   * useLayoutInvalidation). */
  contentRef?: Ref<HTMLDivElement>;
  totalSize: number;
  translateY: number;
}) {
  return (
    <ReadalongScrollContainer
      ref={scrollRef}
      {...scrollAreaProps}
      style={{
        height: '100%',
        width: '100%',
        overflowY: 'auto',
        contain: 'strict',
      }}
    >
      {scrollHeader && <div ref={scrollHeaderRef}>{scrollHeader}</div>}
      <div
        ref={contentRef}
        className={scrollContainerParentClassName}
        style={{
          height: totalSize,
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          className={scrollContainerClassName}
          style={{
            position: 'absolute',
            top: 0,
            transform: `translateY(${(translateY - (scrollMargin ?? 0)).toString()}px)`,
          }}
        >
          {children}
        </div>
      </div>
      {scrollFooter}
    </ReadalongScrollContainer>
  );
}
